import { lessonsFor, mc, tf } from "./helpers";
import type { LessonContentMeta } from "./types";

const guide = { label: "AI Safety Guide", href: "/security-guide" };
const checklist = { label: "Security Checklist", href: "/security-checklist" };

const REVIEWED = "2026-10-12";

const cisa: LessonContentMeta = {
  contentVersion: "1.0",
  author: "GenieBot Learning",
  lastReviewedAt: REVIEWED,
  nextReviewAt: "2027-04-12",
  freshness: "review-semiannually",
  sources: [
    { title: "Secure Our World", organization: "CISA", url: "https://www.cisa.gov/secure-our-world" },
    { title: "Digital Identity Guidelines (SP 800-63)", organization: "NIST", url: "https://pages.nist.gov/800-63-4/" },
  ],
};

/** Cybersecurity expansion. Appended to the track in learningHub.ts. */
export const CYBERSECURITY_EXPANDED = lessonsFor("cybersecurity", [
  {
    slug: "passkeys-and-mfa",
    title: "Passkeys and Phishing-Resistant MFA",
    minutes: 6,
    learn: ["Why passwords alone are weak", "Which MFA methods resist phishing best"],
    explanation: [
      "Passwords can be guessed, reused, leaked and phished. Multi-factor authentication (MFA) adds a second proof — but not all MFA is equal. Text-message codes can be intercepted or phished; authenticator apps are generally stronger.",
      "Passkeys (built on FIDO/WebAuthn standards) are designed to be phishing-resistant: the credential is bound to the real site, so a fake lookalike page can't trick it. For your most valuable accounts — email, banking, work — consider passkeys or a hardware security key where offered. Implementations differ between providers, so check what your account supports.",
    ],
    example: "After turning on a passkey for her email, Maya's sign-in only works on the real site — a convincing fake login page simply can't use it.",
    check: mc("single", "Which sign-in method is generally the most phishing-resistant?", ["Password plus SMS code", "*Passkey or hardware security key", "A very long password alone"], "Passkeys and security keys are bound to the real site, so phishing pages can't replay them. SMS codes can be phished in real time."),
    takeaway: "Turn on MFA everywhere, and prefer passkeys or security keys for the accounts that matter most.",
    tool: checklist,
    resource: guide,
    meta: { ...cisa, sources: [...(cisa.sources ?? []), { title: "FIDO Alliance passkey guidance", organization: "FIDO Alliance", url: "https://fidoalliance.org/passkeys/" }] },
  },
  {
    slug: "qr-code-phishing",
    title: "QR-Code Phishing (Quishing)",
    minutes: 4,
    learn: ["Why QR codes hide their destination", "How to treat unexpected codes like unexpected links"],
    explanation: [
      "A QR code is just a link you can't read. Attackers put malicious codes on parking meters, in emails and on flyers because you can't see where they lead before scanning.",
      "Treat an unexpected QR code like an unexpected link: check the source, preview the URL your camera shows before opening it, and when possible use the official app or type the known address yourself.",
    ],
    example: "A sticker on a parking meter offers 'quick payment.' Instead of scanning, the driver opens the city's official parking app directly.",
    interactive: "would-you-scan",
    check: tf("A QR code on an official-looking sign is always safe to scan.", false, "Stickers are cheap and codes hide their destination. Verify the source or use the official app instead."),
    takeaway: "Unexpected QR code = unexpected link. Verify the source first.",
    tool: checklist,
    resource: guide,
    meta: cisa,
  },
  {
    slug: "account-recovery",
    title: "Account Recovery and Backup Codes",
    minutes: 5,
    learn: ["Keep recovery email and phone details current", "Store backup codes somewhere safe"],
    explanation: [
      "Recovery settings are how you get back into a locked account — and how attackers try to take one over. An old recovery address you no longer control is a open door.",
      "Once a year: review recovery emails and phone numbers on important accounts, generate backup codes where offered, and store them somewhere safe and offline — a password manager or a printed copy in a secure place, not in your email inbox.",
    ],
    example: "Sam loses his phone with his authenticator app. Because he saved backup codes in his password manager, he signs in and re-sets MFA in minutes.",
    check: mc("single", "Where's the safest place for backup codes?", ["In your email inbox", "*In a password manager or a printed copy stored securely", "In a phone note titled 'codes'"], "If your email is the account that's compromised, codes stored there help the attacker. Keep them separate and secure."),
    takeaway: "Review recovery settings yearly and keep backup codes safe and separate.",
    tool: checklist,
    resource: guide,
    meta: cisa,
  },
  {
    slug: "tech-support-scams",
    title: "Tech-Support and Remote-Access Scams",
    minutes: 5,
    learn: ["Spot fake security warnings and unsolicited 'support'", "Why remote-access and gift-card requests are red flags"],
    explanation: [
      "Scammers pose as tech support — through pop-up warnings, phone calls or emails — claiming your device is infected or your account is charged. Real companies don't cold-call you about viruses.",
      "Red flags: urgency, requests to install remote-control software, and any demand for payment by gift card, wire or cryptocurrency. Hang up, close the pop-up (or the browser), and if worried, contact the company through the official number on their website or your bill.",
    ],
    example: "A pop-up screams 'VIRUS DETECTED — call this number.' Instead, Priya closes the browser and runs her own antivirus scan. Nothing is wrong.",
    interactive: "tech-support-scam",
    check: mc("scenario", "A caller says they're from your bank's fraud team and asks you to install an app so they can 'secure your account.' What do you do?", ["Follow along — they called about fraud", "*Hang up and call the bank back on the official number", "Install it but watch what they do"], "Legitimate support never needs remote access from an unsolicited call. Verify through official channels you initiate."),
    takeaway: "Unsolicited 'support' plus urgency plus payment or remote access = scam. Verify through official channels.",
    tool: checklist,
    resource: guide,
    meta: cisa,
  },
  {
    slug: "backups-and-ransomware",
    title: "Backups and Ransomware Basics",
    minutes: 5,
    learn: ["Why backups are your ransomware safety net", "Simple habits: copies, updates, quick reporting"],
    explanation: [
      "Ransomware locks your files and demands payment. The single best defense is a backup you can restore from — keep more than one copy of important files, with at least one disconnected from your computer or in a cloud service with version history.",
      "Keep software updated (updates patch the holes ransomware uses), be cautious with unexpected attachments, and if you suspect an infection, disconnect from the network and report it quickly — at work, tell IT immediately; speed matters more than embarrassment.",
    ],
    example: "A small business gets hit by ransomware on Monday. Because their files back up to the cloud nightly with version history, they restore Friday's copies and lose almost nothing.",
    check: mc("multi", "Which habits actually help against ransomware? (Choose all that apply)", ["*Keep more than one backup copy, one offline or versioned", "*Install software updates promptly", "Pay quickly so files come back", "*Report a suspected infection immediately"], "Payment is unreliable and funds crime. Backups, updates and fast reporting are what actually protect you."),
    takeaway: "Backups you can restore from turn ransomware from a disaster into an inconvenience.",
    tool: checklist,
    resource: guide,
    meta: cisa,
  },
  {
    slug: "identity-theft-response",
    title: "Identity-Theft First Response",
    minutes: 5,
    learn: ["The first moves when your identity or accounts are compromised", "Which official channels to use"],
    explanation: [
      "If you suspect identity theft, act in order: secure the affected accounts (change passwords, turn on MFA), contact your bank or card provider through their official number, and preserve evidence — screenshots, emails, statements.",
      "In the US, IdentityTheft.gov walks you through a recovery plan and lets you place fraud alerts or credit freezes with the credit bureaus. Other countries have equivalents — use official government and financial institution channels, not numbers from the suspicious message itself. Every case is different; a checklist is a starting point, not a guarantee.",
    ],
    example: "After a data-breach letter, Jordan changes the reused password everywhere, freezes credit at the bureaus, and files a report at IdentityTheft.gov.",
    check: mc("single", "Your card details were stolen. What's the best first step?", ["Wait to see if anything happens", "*Contact the bank through its official number and secure the account", "Post about it online for advice"], "Fast, official-channel action limits the damage. Advice from strangers can wait."),
    takeaway: "Secure accounts, notify providers through official channels, preserve evidence, and use government recovery resources.",
    tool: checklist,
    resource: guide,
    meta: cisa,
  },
]);
